/**
 * Shared diagnosis code utilities for diagnosis dropdowns.
 *
 * Source: CMS Medicare Coverage Database Billing and Coding Article A57480
 * (Psychiatry and Psychology Services), linked to LCD L34616.
 * Inclusion means the code is listed in the configured Medicare coverage article
 * for those behavioral-health services — not guaranteed coverage or payment.
 *
 * Regenerate via: npm run terminology:medicare-bh-diagnoses (medplum-server),
 * then refresh `medicare-diagnoses/medicare-bh-diagnoses-a57480.json` from the
 * ValueSet compose concepts (see scripts/terminology).
 */
import medicareBhDiagnoses from './medicare-diagnoses/medicare-bh-diagnoses-a57480.json';

export type DiagnosisItem = { value: string; label: string; description: string };

export interface MedicareDiagnosisCatalogMeta {
  version: string;
  articleId: string;
  lcdId: string;
  effectiveDate: string;
  checksum: string;
  codeCount: number;
}

type CatalogFile = {
  version: string;
  articleId: string;
  lcdId: string;
  effectiveDate: string;
  checksum: string;
  codes: Array<{ code: string; display: string }>;
};

const catalog = medicareBhDiagnoses as CatalogFile;

export const MEDICARE_DIAGNOSIS_CATALOG_META: MedicareDiagnosisCatalogMeta = {
  version: catalog.version,
  articleId: catalog.articleId,
  lcdId: catalog.lcdId,
  effectiveDate: catalog.effectiveDate,
  checksum: catalog.checksum,
  codeCount: catalog.codes.length,
};

const ALL_CODES: DiagnosisItem[] = catalog.codes.map((entry) => ({
  value: entry.code,
  label: entry.code,
  description: entry.display,
}));

const CODE_INDEX = new Map(ALL_CODES.map((item) => [item.value, item]));

/** Common outpatient mental-health codes pinned when present in the Medicare list. */
const COMMON_CODE_VALUES: string[] = [
  'F32.0', 'F32.1', 'F32.2', 'F32.9',
  'F33.0', 'F33.1', 'F33.2', 'F33.9',
  'F34.1',
  'F32.81',
  'F41.1',
  'F41.0',
  'F41.9',
  'F40.10',
  'F40.00',
  'F43.10', 'F43.11', 'F43.12',
  'F43.20', 'F43.21', 'F43.22', 'F43.23',
  'F43.81',
  'F31.81',
  'F31.0',
  'F31.10',
  'F31.30',
  'F42.9',
  'F60.3',
  'F60.81',
  'F90.0', 'F90.1', 'F90.2',
  'F84.0', 'F84.5',
  'F50.81', 'F50.0', 'F50.2',
  'F10.10', 'F10.20',
  'F20.0',
];

const COMMON_CODE_SET = new Set(COMMON_CODE_VALUES);

const commonItems = COMMON_CODE_VALUES
  .map((code) => CODE_INDEX.get(code))
  .filter((item): item is DiagnosisItem => Boolean(item));

/** Grouped Select data: common codes pinned at top, full Medicare-supported list below. */
export const DIAGNOSIS_SELECT_DATA: { group: string; items: DiagnosisItem[] }[] = [
  {
    group: 'Common Medicare-supported diagnoses',
    items: commonItems,
  },
  {
    group: `Medicare-supported diagnoses (${MEDICARE_DIAGNOSIS_CATALOG_META.articleId})`,
    items: ALL_CODES.filter((item) => !COMMON_CODE_SET.has(item.value)),
  },
];

/** Flat autocomplete options (code + description label). */
export const DIAGNOSIS_AUTOCOMPLETE_DATA: { value: string; label: string }[] =
  DIAGNOSIS_SELECT_DATA.flatMap((group) =>
    group.items.map((item) => ({
      value: item.value,
      label: `${item.value} – ${item.description}`,
    })),
  );

/** Returns the description for a given ICD-10 code from the active catalog, or undefined. */
export function getDiagnosisDescription(code: string): string | undefined {
  if (!code?.trim()) return undefined;
  return CODE_INDEX.get(code.trim())?.description;
}

/** True when the code is in the current Medicare-supported ValueSet. */
export function isMedicareSupportedDiagnosisCode(code: string): boolean {
  return CODE_INDEX.has(code.trim());
}

/**
 * Whether a previously selected code remains selectable for display even if it
 * left the current ValueSet. Historical codes are always displayable.
 */
export function getDiagnosisSelectionStatus(code: string): 'current' | 'historical' | 'unknown' {
  const trimmed = code.trim();
  if (!trimmed) return 'unknown';
  if (CODE_INDEX.has(trimmed)) return 'current';
  return 'historical';
}

/** Help text for diagnosis selectors. */
export function getMedicareDiagnosisHelpText(): string {
  return (
    `Medicare-supported for configured psychiatry/psychology services `
    + `(CMS ${MEDICARE_DIAGNOSIS_CATALOG_META.articleId} / LCD ${MEDICARE_DIAGNOSIS_CATALOG_META.lcdId}, `
    + `effective ${MEDICARE_DIAGNOSIS_CATALOG_META.effectiveDate}, version ${MEDICARE_DIAGNOSIS_CATALOG_META.version}). `
    + `Includes a small Unify outpatient therapy overlay (e.g. F43.81) beyond the base CMS article. `
    + `Selection does not guarantee coverage or payment.`
  );
}
